import {
  CROWDING_EXTRACTION_VALUES,
  CROWDING_VALUES,
  DENTITION_TYPE,
  MIDLINE_IMPROVE_VALUES,
  MIDLINE_VALUES,
  OVERBITE_OPTION_VALUES,
  RESOLVE_CROWDING_VALUES,
  SAGITTAL_RELATIONSHIP_VALUES,
  SPACING_VALUES,
  TOOTH_ATTACHMENT,
  TOOTH_MOVEMENT_RESTRICTION,
  TOOTH_SPACING_VALUES
} from 'constants/prescriptionOptions';
import { UltraTreatmentSpecType } from 'types/UltraPrescription.types';
import { ZenClearUltraApiInput } from 'types/ZenClearUltraApiInput.types';

import { getSagittalRelationshipData } from './ultraPrescription.sagittal.utils';
import { UltraFormData } from './ultraPrescription.shared';
import { formatTeeth } from './ultraPrescription.teeth.utils';

export const getCrowdingSubOptions = (
  position: string,
  data?: UltraFormData
) => {
  const crowding =
    position === 'Lower'
      ? data?.treatmentSpecifications?.crowding?.resolveCrowding?.lower
      : data?.treatmentSpecifications?.crowding?.resolveCrowding?.upper;
  return {
    position: position,
    archExpansion: crowding?.archExpansion,
    proclination: crowding?.proclination,
    molarDistalization: crowding?.molarDistalization?.map(
      (item) => item?.value
    ),
    ipr: {
      value: crowding?.ipr?.Ipr,
      ...((crowding?.ipr?.Ipr === RESOLVE_CROWDING_VALUES.OPTIONAL ||
        crowding?.ipr?.Ipr === RESOLVE_CROWDING_VALUES.PRIMARILY) && {
        primaryIpr: {
          anteriorTeeth: crowding?.ipr?.anteriorTeeth,
          rightPosteriorTeeth: crowding?.ipr?.rightPosteriorTeeth,
          leftPosteriorTeeth: crowding?.ipr?.leftPosteriorTeeth
        }
      })
    }
  };
};

const getUltraMidlinesShifts = (
  midline?: UltraTreatmentSpecType['midline']
): string[] => {
  if (midline?.Midline !== MIDLINE_VALUES.IMPROVE) return [];

  const upperShift = midline?.ImproveMidline?.Upper?.adjustPatient;
  const lowerShift = midline?.ImproveMidline?.Lower?.adjustPatient;

  return [upperShift ?? '', lowerShift ?? ''];
};

const buildOverbitePayload = (
  treatmentSpecifications: UltraTreatmentSpecType | undefined
) => {
  if (!treatmentSpecifications?.overbite?.overBite) return {};
  return {
    ultraOverbite: {
      value: treatmentSpecifications.overbite.overBite
        ? [treatmentSpecifications.overbite.overBite]
        : [],
      ...(treatmentSpecifications.overbite.overBite ===
        OVERBITE_OPTION_VALUES.CORRECT_OPEN_BITE && {
        correctOpenBite: [
          {
            position: 'Upper',
            options: {
              value:
                treatmentSpecifications.overbite.correctOpenBite?.Upper?.upper?.map(
                  (item) => item?.value
                ),
              text: treatmentSpecifications.overbite.correctOpenBite?.Upper
                ?.upperOtherText
            }
          },
          {
            position: 'Lower',
            options: {
              value:
                treatmentSpecifications.overbite.correctOpenBite?.Lower?.lower?.map(
                  (item) => item?.value
                ),
              text: treatmentSpecifications.overbite.correctOpenBite?.Lower
                ?.lowerOtherText
            }
          }
        ]
      }),
      ...(treatmentSpecifications.overbite.overBite ===
        OVERBITE_OPTION_VALUES.IMPROVE_DEEP_BITE && {
        improveDeepBite: [
          {
            position: 'Upper',
            options: {
              value:
                treatmentSpecifications.overbite.ImproveDeepBite?.Upper?.upper?.map(
                  (item) => item?.value
                ),
              anteriorBiteRampsOptions:
                treatmentSpecifications.overbite.ImproveDeepBite?.Upper?.anteriorBiteRamps?.map(
                  (item) => item?.value
                ),
              text: treatmentSpecifications.overbite.ImproveDeepBite?.Upper
                ?.upperOtherText
            }
          },
          {
            position: 'Lower',
            options: {
              value:
                treatmentSpecifications.overbite.ImproveDeepBite?.Lower?.lower?.map(
                  (item) => item?.value
                ),
              text: treatmentSpecifications.overbite.ImproveDeepBite?.Lower
                ?.lowerOtherText
            }
          }
        ]
      })
    }
  };
};

const buildUltraTpvPayload = (
  data: UltraFormData
): Omit<
  ZenClearUltraApiInput,
  'type' | 'clinicalPreferences' | 'specialNotes'
> => {
  const treatmentSpecifications = data?.treatmentSpecifications;
  return {
    ultraAnglesClassification: treatmentSpecifications?.angleClassification,
    ultraPatientDiagnosis:
      treatmentSpecifications?.patientDiagnosis?.diagnosis?.map(
        (item) => item?.value
      ),
    ultraPatientDiagnosisText:
      treatmentSpecifications?.patientDiagnosis?.otherText,
    ultraChiefComplaints:
      treatmentSpecifications?.chiefComplaints?.complaints?.map(
        (item) => item?.value
      ),
    ultraChiefComplaintsText:
      treatmentSpecifications?.chiefComplaints?.otherText,
    ultraMainObjectives:
      treatmentSpecifications?.mainObjectives?.objectives?.map(
        (item) => item?.value
      ),
    ultraMainObjectivesText: treatmentSpecifications?.mainObjectives?.otherText,
    ultraArchesToTreat: treatmentSpecifications?.archesToTreat?.arches,
    needPassiveAligners:
      treatmentSpecifications?.archesToTreat?.passiveAligners,
    ...(!!treatmentSpecifications?.toothInformation?.information && {
      ultraToothInfo: {
        value: treatmentSpecifications?.toothInformation?.information,
        ...(treatmentSpecifications?.toothInformation?.dentitionSelection ===
          DENTITION_TYPE.ADULT_DENTITION && {
          missingTeeth: formatTeeth(
            treatmentSpecifications?.toothInformation?.toothChart
          )
        }),
        ...(treatmentSpecifications?.toothInformation?.dentitionSelection ===
          DENTITION_TYPE.PRIMARY_DENTITION && {
          missingTeeth: formatTeeth(
            treatmentSpecifications?.toothInformation?.primaryToothChart
          )
        }),
        primaryDentition:
          treatmentSpecifications?.toothInformation?.primaryDentition
      }
    }),
    ...(!!treatmentSpecifications?.toothMovementRestriction?.restriction && {
      ultraToothMovementRestrictions: {
        value: treatmentSpecifications?.toothMovementRestriction?.restriction,
        ...(treatmentSpecifications?.toothMovementRestriction?.restriction ===
          TOOTH_MOVEMENT_RESTRICTION.SELECT_TEETH_SHOULD_NOT_BE_MOVED && {
          restrictions: formatTeeth(
            treatmentSpecifications?.toothMovementRestriction?.toothChart
          ),
          primaryDentition:
            formatTeeth(
              treatmentSpecifications?.toothMovementRestriction
                ?.primaryToothChart
            ) ||
            treatmentSpecifications?.toothMovementRestriction?.primaryDentition
        })
      }
    }),
    ...(!!treatmentSpecifications?.toothAttachment?.attachment && {
      ultraToothUnavailableForAttachment: {
        value: treatmentSpecifications?.toothAttachment?.attachment,
        ...(treatmentSpecifications?.toothAttachment?.attachment ===
          TOOTH_ATTACHMENT.NO_ATTACHMENT && {
          noAttachmentValues: formatTeeth(
            treatmentSpecifications?.toothAttachment?.toothChart
          )
        }),

        primaryDentition:
          treatmentSpecifications?.toothAttachment?.primaryDentition
      }
    }),

    ...(!!treatmentSpecifications?.sagittalRelationship?.relationship && {
      ultraSagittalRelationship: {
        value: treatmentSpecifications?.sagittalRelationship?.relationship,
        optionsRowOne: getSagittalRelationshipData(data, false),
        ...(treatmentSpecifications?.sagittalRelationship?.relationship ===
          SAGITTAL_RELATIONSHIP_VALUES.RIGHT_OR_LEFT && {
          optionsRowTwo: getSagittalRelationshipData(data, true)
        })
      }
    }),
    ultraOverjet: treatmentSpecifications?.overjet,
    ...buildOverbitePayload(treatmentSpecifications),

    ...(!!treatmentSpecifications?.midline?.Midline && {
      ultraMidlines: {
        value: treatmentSpecifications?.midline?.Midline,
        ...(treatmentSpecifications?.midline?.Midline ===
          MIDLINE_VALUES.IMPROVE && {
          options: [
            {
              position: 'Upper',
              value:
                treatmentSpecifications?.midline?.ImproveMidline?.Upper?.upper,
              specification:
                treatmentSpecifications?.midline?.ImproveMidline?.Upper
                  ?.upper === MIDLINE_IMPROVE_VALUES.DO_NOT_CORRECT
                  ? undefined
                  : treatmentSpecifications?.midline?.ImproveMidline?.Upper
                      ?.adjustPatient
            },
            {
              position: 'Lower',
              value:
                treatmentSpecifications?.midline?.ImproveMidline?.Lower?.lower,
              specification:
                treatmentSpecifications?.midline?.ImproveMidline?.Lower
                  ?.lower === MIDLINE_IMPROVE_VALUES.DO_NOT_CORRECT
                  ? undefined
                  : treatmentSpecifications?.midline?.ImproveMidline?.Lower
                      ?.adjustPatient
            }
          ]
        })
      },
      ultraMidlinesShifts: getUltraMidlinesShifts(
        treatmentSpecifications?.midline
      )
    }),
    ...(!!treatmentSpecifications?.spacing?.Spacing && {
      ultraSpacing: {
        value: treatmentSpecifications?.spacing?.Spacing
          ? [treatmentSpecifications?.spacing?.Spacing]
          : [],
        ...(treatmentSpecifications?.spacing?.Spacing ===
          SPACING_VALUES.INSTRUCTIONS && {
          options: [
            {
              position: 'Upper',
              value: treatmentSpecifications?.spacing?.upper
            },
            {
              position: 'Lower',
              value: treatmentSpecifications?.spacing?.lower
            }
          ],
          type: treatmentSpecifications?.spacing?.toothSpacing?.space,
          leaveSpacing:
            treatmentSpecifications?.spacing?.toothSpacing?.space ===
            TOOTH_SPACING_VALUES.LEAVE_SPACING
              ? Object.keys(
                  treatmentSpecifications?.spacing?.toothSpacing?.toothChart ||
                    {}
                )
                  ?.filter(
                    (item) =>
                      treatmentSpecifications?.spacing?.toothSpacing
                        ?.toothChart?.[item]
                  )
                  ?.map((item) => ({
                    position: item,
                    value: `${treatmentSpecifications?.spacing?.toothSpacing?.toothChart?.[item]}mm`
                  }))
              : undefined
        })
      }
    }),
    ...(!!treatmentSpecifications?.crowding?.Crowding &&
      treatmentSpecifications?.crowding?.Crowding?.length !== 0 && {
        ultraCrowding: {
          value: treatmentSpecifications?.crowding?.Crowding?.map(
            (item) => item?.value
          ),
          ...(treatmentSpecifications?.crowding?.Crowding?.find(
            (item) => item.value === CROWDING_VALUES.RESOLVE_CROWDING
          ) && {
            options: [
              getCrowdingSubOptions('Upper', data),
              getCrowdingSubOptions('Lower', data)
            ]
          })
        }
      }),
    ...(!!treatmentSpecifications?.extraction?.Extraction && {
      ultraTeethToExtract: {
        value: treatmentSpecifications?.extraction?.Extraction,
        ...(treatmentSpecifications?.extraction?.Extraction ===
          CROWDING_EXTRACTION_VALUES.EXTRACTION && {
          primaryDentition:
            treatmentSpecifications?.extraction?.primaryDentition
        })
      }
    }),
    ...mapAuxiliariesAndAppliancesForApi(
      treatmentSpecifications?.auxiliaries,
      treatmentSpecifications?.additionalAppliances
    )
  };
};

export { buildUltraTpvPayload };

export const mapAuxiliariesAndAppliancesForApi = (
  auxiliaries?: UltraTreatmentSpecType['auxiliaries'],
  additionalAppliances?: UltraTreatmentSpecType['additionalAppliances']
): Pick<
  ZenClearUltraApiInput,
  | 'isAuxiliariesPresent'
  | 'auxiliaryButtons'
  | 'auxiliaryPrecisionCuts'
  | 'isAdditionalApplianceRequired'
  | 'tads'
  | 'sectionalBraces'
  | 'otherAppliances'
> => ({
  ...(auxiliaries?.isPresent !== undefined && {
    isAuxiliariesPresent: auxiliaries.isPresent,
    ...(auxiliaries.isPresent && {
      auxiliaryButtons: formatTeeth(auxiliaries.buttons),
      auxiliaryPrecisionCuts: formatTeeth(auxiliaries.precisionCuts)
    })
  }),
  ...(additionalAppliances?.isRequired !== undefined && {
    isAdditionalApplianceRequired: additionalAppliances.isRequired,
    ...(additionalAppliances.isRequired && {
      tads: additionalAppliances.tads,
      sectionalBraces: additionalAppliances.sectionalBraces,
      otherAppliances: additionalAppliances.otherAppliances
    })
  })
});
